import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, TextField, MenuItem, Button, Alert, Snackbar,
  Grid, Table, TableBody, TableCell, TableHead, TableRow, IconButton, CircularProgress,
  Card, CardContent, Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { eledgerService } from '../services/eledgerService';
import { allocationService } from '../services/allocationService';

function Allocation() {
  const [tabValue, setTabValue] = useState(0);
  const [ledgers, setLedgers] = useState([]);
  const [rules, setRules] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [ruleForm, setRuleForm] = useState({
    e_ledger_id: '',
    rule_name: '',
    allocation_basis: 'mass',
    applicable_scope: 'both'
  });
  const [editingRuleId, setEditingRuleId] = useState(null);

  const [executeForm, setExecuteForm] = useState({
    allocation_rule_id: '',
    products: [{ product_id: '', product_name: '', percentage: '' }]
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [ledgerData, ruleData, execData] = await Promise.all([
        eledgerService.getAll({ status: 'active' }),
        allocationService.getRules(),
        allocationService.getExecutions()
      ]);
      setLedgers(ledgerData ?? []);
      setRules(ruleData ?? []);
      setExecutions(execData ?? []);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const resetRuleForm = () => {
    setRuleForm({ e_ledger_id: '', rule_name: '', allocation_basis: 'mass', applicable_scope: 'both' });
    setEditingRuleId(null);
  };

  const handleSaveRule = async () => {
    try {
      if (editingRuleId) {
        await allocationService.updateRule(editingRuleId, ruleForm);
        setSnackbar({ open: true, message: 'Allocation rule updated', severity: 'success' });
      } else {
        await allocationService.createRule(ruleForm);
        setSnackbar({ open: true, message: 'Allocation rule created', severity: 'success' });
      }
      resetRuleForm();
      loadData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error?.message || `Failed to ${editingRuleId ? 'update' : 'create'} rule`,
        severity: 'error'
      });
    }
  };

  const handleEditRule = (rule) => {
    setEditingRuleId(rule.allocation_rule_id);
    setRuleForm({
      e_ledger_id: rule.e_ledger_id,
      rule_name: rule.rule_name,
      allocation_basis: rule.allocation_basis,
      applicable_scope: rule.applicable_scope ?? 'both'
    });
  };

  const handleDeleteRule = async (id) => {
    try {
      await allocationService.deleteRule(id);
      setSnackbar({ open: true, message: 'Rule deleted', severity: 'success' });
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete rule', severity: 'error' });
    }
  };

  const addProduct = () => {
    setExecuteForm({
      ...executeForm,
      products: [...executeForm.products, { product_id: '', product_name: '', percentage: '' }]
    });
  };

  const updateProduct = (index, field, value) => {
    const newProducts = [...executeForm.products];
    newProducts[index][field] = value;
    setExecuteForm({ ...executeForm, products: newProducts });
  };

  const removeProduct = (index) => {
    const newProducts = executeForm.products.filter((_, i) => i !== index);
    setExecuteForm({ ...executeForm, products: newProducts });
  };

  const handleExecuteAllocation = async () => {
    try {
      const data = {
        allocation_rule_id: executeForm.allocation_rule_id,
        products: executeForm.products.map(p => ({
          ...p,
          product_id: p.product_id || crypto.randomUUID(),
          percentage: parseFloat(p.percentage)
        }))
      };
      
      await allocationService.execute(data);
      setSnackbar({ open: true, message: 'Allocation executed successfully! E-ledger is now fully allocated.', severity: 'success' });
      setExecuteForm({ allocation_rule_id: '', products: [{ product_id: '', product_name: '', percentage: '' }] });
      loadData();
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.error?.message || 'Failed to execute allocation', 
        severity: 'error' 
      });
    }
  };

  const totalPercentage = executeForm.products.reduce((sum, p) => sum + (parseFloat(p.percentage) || 0), 0);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Allocation</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Allocate emissions to outputs using causal logic (E-ledger requires 100% allocation)
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Allocation Rules" />
          <Tab label="Execute Allocation" />
          <Tab label="Execution History" />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>{editingRuleId ? 'Edit Rule' : 'Create Rule'}</Typography>
              <TextField
                select
                label="E-Ledger"
                fullWidth
                margin="normal"
                value={ruleForm.e_ledger_id}
                onChange={(e) => setRuleForm({ ...ruleForm, e_ledger_id: e.target.value })}
              >
                {ledgers.map((l) => (
                  <MenuItem key={l.e_ledger_id} value={l.e_ledger_id}>{l.ledger_name}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Rule Name"
                fullWidth
                margin="normal"
                value={ruleForm.rule_name}
                onChange={(e) => setRuleForm({ ...ruleForm, rule_name: e.target.value })}
              />
              <TextField
                select
                label="Allocation Basis"
                fullWidth
                margin="normal"
                value={ruleForm.allocation_basis}
                onChange={(e) => setRuleForm({ ...ruleForm, allocation_basis: e.target.value })}
              >
                <MenuItem value="mass">Mass-based</MenuItem>
                <MenuItem value="energy_content">Energy Content</MenuItem>
                <MenuItem value="economic_value">Economic Value</MenuItem>
                <MenuItem value="exergy">Exergy</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </TextField>
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button variant="contained" onClick={handleSaveRule} disabled={!ruleForm.e_ledger_id || !ruleForm.rule_name}>
                  {editingRuleId ? 'Update Rule' : 'Create Rule'}
                </Button>
                {editingRuleId && (
                  <Button variant="outlined" onClick={resetRuleForm}>
                    Cancel
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Existing Rules</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rule Name</TableCell>
                    <TableCell>Basis</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rules.map((rule) => (
                    <TableRow key={rule.allocation_rule_id}>
                      <TableCell>{rule.rule_name}</TableCell>
                      <TableCell><Chip label={rule.allocation_basis} size="small" /></TableCell>
                      <TableCell><Chip label={rule.active ? 'Active' : 'Inactive'} size="small" color={rule.active ? 'success' : 'default'} /></TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleEditRule(rule)} aria-label="edit rule">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteRule(rule.allocation_rule_id)} aria-label="delete rule">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Execute Allocation</Typography>
              <TextField
                select
                label="Select Rule"
                fullWidth
                margin="normal"
                value={executeForm.allocation_rule_id}
                onChange={(e) => setExecuteForm({ ...executeForm, allocation_rule_id: e.target.value })}
              >
                {rules.filter(r => r.active).map((r) => (
                  <MenuItem key={r.allocation_rule_id} value={r.allocation_rule_id}>{r.rule_name}</MenuItem>
                ))}
              </TextField>

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Products</Typography>
              {executeForm.products.map((product, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                  <Grid item xs={5}>
                    <TextField
                      label="Product Name"
                      size="small"
                      fullWidth
                      value={product.product_name}
                      onChange={(e) => updateProduct(index, 'product_name', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      type="number"
                      label="Percentage"
                      size="small"
                      fullWidth
                      value={product.percentage}
                      onChange={(e) => updateProduct(index, 'percentage', e.target.value)}
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <IconButton onClick={() => removeProduct(index)} disabled={executeForm.products.length === 1}>
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
              
              <Button startIcon={<AddIcon />} onClick={addProduct} sx={{ mt: 1 }}>
                Add Product
              </Button>

              <Alert severity={totalPercentage === 100 ? 'success' : 'warning'} sx={{ mt: 2 }}>
                Total: {totalPercentage.toFixed(2)}% {totalPercentage === 100 ? '(Valid)' : '(Must be 100%)'}
              </Alert>

              <Button 
                variant="contained" 
                onClick={handleExecuteAllocation} 
                sx={{ mt: 2 }}
                disabled={!executeForm.allocation_rule_id || totalPercentage !== 100}
              >
                Execute Allocation
              </Button>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>E-Ledger Rule</Typography>
                <Alert severity="info">
                  Allocations must total <strong>100%</strong> per E-ledger principles. All emissions must be fully allocated to outputs.
                </Alert>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tabValue === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Execution History</Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Rule</TableCell>
                <TableCell align="right">Total (kg CO2e)</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {executions.map((exec) => (
                <TableRow key={exec.allocation_execution_id}>
                  <TableCell>{exec.execution_date}</TableCell>
                  <TableCell>{exec.rule?.rule_name || 'N/A'}</TableCell>
                  <TableCell align="right">{parseFloat(exec.total_emissions_kg_co2e).toFixed(4)}</TableCell>
                  <TableCell><Chip label={exec.status} size="small" color={exec.status === 'completed' ? 'success' : 'default'} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default Allocation;
